/**
 * The safe-to-print gate checklist — two panels, not one list
 * (docs/features/prepress-orchestration/17_safe_to_print_gate.md §"The
 * checklist"). Left/top panel is read-only evidence the system already
 * asserts; bottom panel is the six things only a human can confirm. "Safe to
 * print" stays disabled until all six are ticked — that gating is the whole
 * point of the gate, so it is shown, not just enforced (the server enforces
 * it for real: an incomplete or stale-version submission 400s).
 *
 * Owns its own data: the checklist definition (GET
 * /prepress/runs/safe-to-print-checklist — business wording is backend-owned,
 * frontend-architecture.md "Business logic stays backend") and the two
 * terminal actions:
 *  - Hold -> POST /prepress/runs/{run_id}/hold with a required reason. Keeps
 *    the job parked at the gate (not a rejection) — see the "the decision,
 *    from prepress" note in the design doc.
 *  - Safe to print -> POST /prepress/runs/{run_id}/safe-to-print with the
 *    confirmed item ids, checklist_version, hand_worked (+ reason), notes,
 *    and the artefact refs shown in the evidence panel.
 * `resumeProblemRun` must never be called with `stage: "safe_to_print"` at
 * all — the backend 400s that on purpose, for both decisions, since each now
 * has its own dedicated route.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, Pause, TriangleAlert } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  PrepressGateEvidenceItem,
  PrepressGateJob,
  SafeToPrintChecklist,
} from "@shared-core/data/api-types";
import {
  PREPRESS_GATE_HAND_WORK_ITEM_ID,
  PREPRESS_SITE_LABELS,
} from "@shared-core/mock-data/prepress-home";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "../../primitives/sheet";
import { Checkbox } from "../../primitives/checkbox";
import { Label } from "../../primitives/label";
import { Input } from "../../primitives/input";
import { Textarea } from "../../primitives/textarea";
import { Button } from "../../primitives/button";
import { Badge } from "../../primitives/badge";
import { Alert, AlertDescription, AlertTitle } from "../../primitives/alert";

interface PrepressGateDrawerProps {
  job: PrepressGateJob | null;
  onOpenChange: (open: boolean) => void;
  /** The job was put on hold — the caller should drop it from any locally
   *  held safe_to_print list and refresh the board. */
  onHeld: (jobReference: string) => void;
  /** The job was signed off as safe to print — same refresh contract. */
  onSignedOff: (jobReference: string) => void;
}

export function PrepressGateDrawer({
  job,
  onOpenChange,
  onHeld,
  onSignedOff,
}: PrepressGateDrawerProps) {
  const adapter = useDataAdapter();
  const [checklist, setChecklist] = useState<SafeToPrintChecklist | null>(null);
  const [checklistError, setChecklistError] = useState<string | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [handImposed, setHandImposed] = useState(false);
  const [handImposedReason, setHandImposedReason] = useState("");
  const [notes, setNotes] = useState("");
  const [holdReason, setHoldReason] = useState("");
  const [submitting, setSubmitting] = useState<"hold" | "safe" | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Which panel's inputs the current submitError belongs to, so the
  // offending inputs can carry aria-invalid/aria-describedby alongside the
  // banner (.claude/rules/api-validation.md's frontend section) — a hold
  // failure is almost always the reason field, a safe-to-print failure is
  // almost always the confirm checklist (incomplete or a stale version).
  const [submitErrorFor, setSubmitErrorFor] = useState<"hold" | "safe" | null>(
    null,
  );

  // The checklist definition is process-global, not per-job — fetch it once
  // rather than refetching every time the drawer opens on a new job.
  useEffect(() => {
    let cancelled = false;
    adapter
      .getSafeToPrintChecklist()
      .then((res) => {
        if (!cancelled) setChecklist(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setChecklistError(
            err instanceof Error
              ? err.message
              : "Failed to load the confirm checklist",
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset the form whenever a different job opens — one tick per job
  // (decision 3), never carried over from whatever was open before.
  useEffect(() => {
    setChecked({});
    setHandImposed(false);
    setHandImposedReason("");
    setNotes("");
    setHoldReason("");
    setSubmitError(null);
    setSubmitErrorFor(null);
  }, [job?.run_id]);

  if (!job) return null;

  const items = checklist?.items ?? [];
  const confirmedCount = items.filter((item) => checked[item.id]).length;
  const allConfirmed = items.length > 0 && confirmedCount === items.length;

  function artefactRefs(): Record<string, string> {
    const refs: Record<string, string> = {};
    for (const item of job!.evidence) {
      if (item.reference) refs[item.id] = item.reference;
    }
    return refs;
  }

  const canHold = holdReason.trim().length > 0;

  async function handleHold() {
    if (!canHold) return;
    setSubmitting("hold");
    setSubmitError(null);
    setSubmitErrorFor(null);
    try {
      await adapter.holdSafeToPrint(job!.run_id, {
        reason: holdReason.trim(),
      });
      onHeld(job!.job_reference);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to put this job on hold";
      setSubmitError(message);
      setSubmitErrorFor("hold");
    } finally {
      setSubmitting(null);
    }
  }

  async function handleSafeToPrint() {
    if (!checklist || !allConfirmed) return;
    setSubmitting("safe");
    setSubmitError(null);
    setSubmitErrorFor(null);
    try {
      await adapter.recordSafeToPrint(job!.run_id, {
        checklistVersion: checklist.checklist_version,
        confirmedItemIds: items.map((item) => item.id),
        handWorked: handImposed,
        handWorkedReason: handImposed
          ? handImposedReason.trim() || undefined
          : undefined,
        artefactRefs: artefactRefs(),
        notes: notes.trim() || undefined,
      });
      onSignedOff(job!.job_reference);
    } catch (err) {
      // A 400 (incomplete checklist, stale checklist_version) is a real,
      // expected error state — surfaced here, not swallowed. The button
      // stays disabled until every item is ticked client-side, but the
      // server is the actual enforcement.
      const message =
        err instanceof Error
          ? err.message
          : "Failed to record this safe-to-print sign-off";
      setSubmitError(message);
      setSubmitErrorFor("safe");
    } finally {
      setSubmitting(null);
    }
  }

  return (
    <Sheet open={!!job} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            Safe to print — {job.job_reference} · {job.customer_name ?? "—"} ·{" "}
            {job.product_name ?? "—"}
          </SheetTitle>
          <p className="text-sm text-muted-foreground">
            {PREPRESS_SITE_LABELS[job.site]} · rev {job.revision}
          </p>
        </SheetHeader>

        <div className="px-4 space-y-6">
          {job.hold && (
            <Alert>
              <Pause />
              <AlertTitle>
                On hold — {job.hold.operator},{" "}
                {formatDistanceToNowStrict(new Date(job.hold.held_at))} ago
              </AlertTitle>
              <AlertDescription>{job.hold.reason}</AlertDescription>
            </Alert>
          )}

          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              What the system checked{" "}
              <span className="normal-case font-normal">
                (read-only evidence)
              </span>
            </h3>
            <div className="space-y-2">
              {job.evidence.map((item) => (
                <EvidenceRow key={item.id} item={item} />
              ))}
            </div>
          </section>

          <section className="space-y-2">
            <div className="flex items-baseline justify-between">
              <h3
                id="gate-confirm-checklist-heading"
                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                What you're confirming{" "}
                <span className="normal-case font-normal">
                  (only a human knows)
                </span>
              </h3>
              <span className="text-xs text-muted-foreground">
                {items.length > 0
                  ? `${confirmedCount} of ${items.length} confirmed`
                  : "—"}
              </span>
            </div>
            {checklistError ? (
              <Alert variant="destructive">
                <TriangleAlert />
                <AlertTitle>Couldn't load the confirm checklist</AlertTitle>
                <AlertDescription>{checklistError}</AlertDescription>
              </Alert>
            ) : items.length === 0 ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <div
                role="group"
                aria-labelledby="gate-confirm-checklist-heading"
                className="space-y-3"
                aria-invalid={submitErrorFor === "safe"}
                aria-describedby={
                  submitErrorFor === "safe" ? "gate-submit-error" : undefined
                }
              >
                {items.map((item) => (
                  <div key={item.id} className="space-y-2">
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={!!checked[item.id]}
                        onCheckedChange={(v) =>
                          setChecked((prev) => ({ ...prev, [item.id]: !!v }))
                        }
                      />
                      <span>{item.label}</span>
                    </label>
                    {item.id === PREPRESS_GATE_HAND_WORK_ITEM_ID && (
                      <div className="ml-6 space-y-2">
                        <label className="flex items-start gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={handImposed}
                            onCheckedChange={(v) => setHandImposed(!!v)}
                          />
                          <span>this job was imposed by hand (XMF)</span>
                        </label>
                        {handImposed && (
                          <Input
                            value={handImposedReason}
                            onChange={(e) =>
                              setHandImposedReason(e.target.value)
                            }
                            placeholder="why?"
                            className="max-w-sm"
                            aria-label="Reason for hand imposition"
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="space-y-1.5">
            <Label htmlFor="gate-notes">Notes</Label>
            <Textarea
              id="gate-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional — included with a safe-to-print sign-off"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="gate-hold-reason">Hold reason</Label>
            <Textarea
              id="gate-hold-reason"
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              rows={2}
              placeholder="Required to hold — what needs checking or fixing before this can be signed off"
              aria-invalid={submitErrorFor === "hold"}
              aria-describedby={
                submitErrorFor === "hold" ? "gate-submit-error" : undefined
              }
            />
          </div>

          {submitError && (
            <Alert id="gate-submit-error" variant="destructive">
              <TriangleAlert />
              <AlertTitle>Couldn't record that decision</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}
        </div>

        <SheetFooter className="flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleHold}
            disabled={!canHold || submitting !== null}
          >
            {submitting === "hold" ? "Holding…" : "Hold"}
          </Button>
          <Button
            disabled={!allConfirmed || submitting !== null}
            onClick={handleSafeToPrint}
          >
            {submitting === "safe" ? "Recording…" : "Safe to print"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function EvidenceRow({ item }: { item: PrepressGateEvidenceItem }) {
  const isWarning = item.status === "warning";
  return (
    <div className="flex items-start gap-2 text-sm">
      {isWarning ? (
        <TriangleAlert className="size-4 text-amber-600 shrink-0 mt-0.5" />
      ) : (
        <CheckCircle2 className="size-4 text-green-600 shrink-0 mt-0.5" />
      )}
      <div className="min-w-0 flex-1">
        <span className="font-medium capitalize">{item.label}</span>{" "}
        {isWarning ? (
          <Badge variant="warning" className="align-middle">
            {item.detail}
          </Badge>
        ) : (
          <span className="text-muted-foreground">{item.detail}</span>
        )}
      </div>
      {item.reference && (
        <span className="text-xs text-muted-foreground shrink-0">
          {item.reference}
        </span>
      )}
    </div>
  );
}
